import { useState, useEffect, useRef } from 'react';
import { usePark } from '../contexts/ParkContext';
import axios from 'axios';
import { 
  Plus, 
  Droplets,
  Gauge,
  FileText,
  Camera,
  Trash2,
  Download,
  Search,
  Calendar,
  DollarSign,
  Eye,
  Pencil,
  CreditCard,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { generateWaterReceiptPDF } from '../utils/generateWaterPDF';

const API = process.env.REACT_APP_BACKEND_URL;

const meses = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN'
  }).format(amount || 0);
};

const Agua = () => {
  const { selectedPark, selectedYear } = usePark();
  const [meters, setMeters] = useState([]);
  const [readings, setReadings] = useState([]);
  const [lots, setLots] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(0);
  const [priceHistory, setPriceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showMeterModal, setShowMeterModal] = useState(false);
  const [showReadingModal, setShowReadingModal] = useState(false);
  const [showEditReadingModal, setShowEditReadingModal] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedReading, setSelectedReading] = useState(null);
  const [editingReading, setEditingReading] = useState(null);
  const [payingReading, setPayingReading] = useState(null);
  
  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    fecha: new Date().toISOString().split('T')[0],
    metodo_pago: 'transferencia',
    referencia: ''
  });
  
  // Forms
  const [meterForm, setMeterForm] = useState({
    numero_medidor: '',
    lot_id: '',
    ubicacion: ''
  });
  
  const [readingForm, setReadingForm] = useState({
    meter_id: '',
    lectura_anterior: '',
    lectura_actual: '',
    fecha_lectura: new Date().toISOString().split('T')[0],
    mes: new Date().getMonth() + 1,
    año: new Date().getFullYear()
  });
  
  const [priceForm, setPriceForm] = useState({
    precio_m3: '',
    fecha_vigencia: new Date().toISOString().split('T')[0]
  });
  
  // Photo upload
  const fileInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [tempPhoto, setTempPhoto] = useState(null);
  
  // Selection for bulk delete
  const [selectedReadings, setSelectedReadings] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Filter
  const [filterMes, setFilterMes] = useState('');

  const fetchData = async () => {
    if (!selectedPark) return;
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [metersRes, readingsRes, lotsRes, priceRes, priceHistoryRes] = await Promise.all([
        axios.get(`${API}/api/water/meters?park_id=${selectedPark.id}`, { headers }),
        axios.get(`${API}/api/water/readings?park_id=${selectedPark.id}&año=${selectedYear}`, { headers }),
        axios.get(`${API}/api/lots?park_id=${selectedPark.id}`, { headers }),
        axios.get(`${API}/api/water/prices/current?park_id=${selectedPark.id}`, { headers }),
        axios.get(`${API}/api/water/prices?park_id=${selectedPark.id}`, { headers })
      ]);
      
      setMeters(metersRes.data);
      setReadings(readingsRes.data);
      setLots(lotsRes.data);
      setCurrentPrice(priceRes.data.precio_m3 || 0);
      setPriceHistory(priceHistoryRes.data);
    } catch (error) {
      console.error('Error fetching water data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedPark, selectedYear]);

  // Meter handlers
  const handleCreateMeter = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/water/meters`, {
        ...meterForm,
        park_id: selectedPark.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Medidor creado exitosamente');
      setShowMeterModal(false);
      setMeterForm({ numero_medidor: '', lot_id: '', ubicacion: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear el medidor');
    }
  };

  const handleDeleteMeter = async (meterId) => {
    if (!window.confirm('¿Estás seguro de eliminar este medidor?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/water/meters/${meterId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Medidor eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar el medidor');
    }
  };

  // Reading handlers
  const handleCreateReading = async (e) => {
    e.preventDefault();
    
    if (!tempPhoto) {
      toast.error('La fotografía del medidor es obligatoria');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Create reading
      const response = await axios.post(`${API}/api/water/readings`, {
        ...readingForm,
        lectura_anterior: parseFloat(readingForm.lectura_anterior),
        lectura_actual: parseFloat(readingForm.lectura_actual)
      }, { headers });
      
      // Upload photo
      const formData = new FormData();
      formData.append('file', tempPhoto);
      await axios.post(`${API}/api/water/readings/${response.data.id}/upload-photo`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Lectura registrada y recibo generado');
      setShowReadingModal(false);
      resetReadingForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar la lectura');
    }
  };

  const resetReadingForm = () => {
    setReadingForm({
      meter_id: '',
      lectura_anterior: '',
      lectura_actual: '',
      fecha_lectura: new Date().toISOString().split('T')[0],
      mes: new Date().getMonth() + 1,
      año: new Date().getFullYear()
    });
    setTempPhoto(null);
  };

  // Edit reading handlers
  const handleEditReading = (reading) => {
    setEditingReading({
      ...reading,
      lectura_anterior: reading.lectura_anterior.toString(),
      lectura_actual: reading.lectura_actual.toString()
    });
    setShowEditReadingModal(true);
  };

  const handleUpdateReading = async (e) => {
    e.preventDefault();
    if (!editingReading) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Update reading
      await axios.put(`${API}/api/water/readings/${editingReading.id}`, {
        lectura_anterior: parseFloat(editingReading.lectura_anterior),
        lectura_actual: parseFloat(editingReading.lectura_actual),
        fecha_lectura: editingReading.fecha_lectura,
        mes: editingReading.mes,
        año: editingReading.año
      }, { headers });
      
      // Upload new photo if selected
      if (tempPhoto) {
        const formData = new FormData();
        formData.append('file', tempPhoto);
        await axios.post(`${API}/api/water/readings/${editingReading.id}/upload-photo`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success('Lectura actualizada');
      setShowEditReadingModal(false);
      setEditingReading(null);
      setTempPhoto(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar la lectura');
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTempPhoto(file);
    }
  };

  // When selecting a meter, auto-fill last reading
  const handleMeterSelect = async (meterId) => {
    setReadingForm(prev => ({ ...prev, meter_id: meterId }));
    
    // Find last reading for this meter
    const meterReadings = readings.filter(r => r.meter_id === meterId);
    if (meterReadings.length > 0) {
      const lastReading = meterReadings.sort((a, b) => {
        if (a.año !== b.año) return b.año - a.año;
        return b.mes - a.mes;
      })[0];
      setReadingForm(prev => ({
        ...prev,
        lectura_anterior: lastReading.lectura_actual.toString()
      }));
    }
  };

  // Price handlers
  const handleCreatePrice = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/water/prices`, {
        ...priceForm,
        precio_m3: parseFloat(priceForm.precio_m3),
        park_id: selectedPark.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Precio actualizado');
      setShowPriceModal(false);
      setPriceForm({ precio_m3: '', fecha_vigencia: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar el precio');
    }
  };

  // Bulk delete
  const toggleReadingSelection = (readingId) => {
    setSelectedReadings(prev => 
      prev.includes(readingId) 
        ? prev.filter(id => id !== readingId)
        : [...prev, readingId]
    );
  };

  const toggleSelectAllReadings = () => {
    const filteredReadings = filterMes && filterMes !== 'all'
      ? readings.filter(r => r.mes === parseInt(filterMes))
      : readings;
    
    if (selectedReadings.length === filteredReadings.length) {
      setSelectedReadings([]);
    } else {
      setSelectedReadings(filteredReadings.map(r => r.id));
    }
  };

  const handleDeleteSelectedReadings = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      for (const id of selectedReadings) {
        await axios.delete(`${API}/api/water/readings/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success(`${selectedReadings.length} lecturas eliminadas`);
      setSelectedReadings([]);
      setShowDeleteDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar las lecturas');
    } finally {
      setDeleting(false);
    }
  };

  // View receipt
  const handleViewReceipt = async (reading) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/water/readings/${reading.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedReading(response.data);
      setShowReceiptModal(true);
    } catch (error) {
      toast.error('Error al cargar el recibo');
    }
  };

  // Download PDF - fetch full data first
  const handleDownloadPDF = async (reading) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/water/readings/${reading.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      generateWaterReceiptPDF(selectedPark?.nombre || 'Parque', response.data);
    } catch (error) {
      // Fallback to passed reading if fetch fails
      generateWaterReceiptPDF(selectedPark?.nombre || 'Parque', reading);
    }
  };

  // Payment handlers
  const handleOpenPayment = (reading) => {
    setPayingReading(reading);
    setPaymentForm({
      fecha: new Date().toISOString().split('T')[0],
      metodo_pago: 'transferencia',
      referencia: ''
    });
    setShowPaymentModal(true);
  };

  const handleRegisterPayment = async (e) => {
    e.preventDefault();
    if (!payingReading) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/water/readings/${payingReading.id}/pay`, {
        reading_id: payingReading.id,
        ...paymentForm
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Pago registrado exitosamente');
      setShowPaymentModal(false);
      setPayingReading(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar el pago');
    }
  };

  // Filtered readings
  const filteredReadings = filterMes && filterMes !== 'all'
    ? readings.filter(r => r.mes === parseInt(filterMes))
    : readings;

  // Calculate totals
  const totalConsumo = readings.reduce((sum, r) => sum + (r.consumo_m3 || 0), 0);
  const totalCobrado = readings.reduce((sum, r) => sum + (r.monto_total || 0), 0);

  if (!selectedPark) {
    return (
      <div className="p-8">
        <div className="card-industrial text-center py-16">
          <Droplets size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-bold mb-2">Selecciona un parque</h3>
          <p className="text-slate-500">Elige un parque del menú lateral para ver el módulo de agua.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Agua</h1>
          <p className="page-subtitle">{selectedPark.nombre} - Año {selectedYear}</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="card-industrial">
          <span className="label-overline">Medidores Activos</span>
          <p className="text-3xl font-black tracking-tight mt-2">
            {meters.filter(m => m.activo).length}
          </p>
        </div>
        <div className="card-industrial">
          <span className="label-overline">Lecturas del Año</span>
          <p className="text-3xl font-black tracking-tight mt-2">
            {readings.length}
          </p>
        </div>
        <div className="card-industrial">
          <span className="label-overline">Consumo Total</span>
          <p className="text-3xl font-black tracking-tight mt-2 text-blue-600">
            {totalConsumo.toLocaleString()} m³
          </p>
        </div>
        <div className="card-industrial">
          <span className="label-overline">Total Facturado</span>
          <p className="text-3xl font-black tracking-tight mt-2 text-emerald-600">
            {formatCurrency(totalCobrado)}
          </p>
          <p className="text-xs text-slate-500 mt-1">Precio actual: {formatCurrency(currentPrice)}/m³</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="medidores" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 rounded-none">
          <TabsTrigger value="medidores" className="rounded-none data-[state=active]:bg-white">
            <Gauge size={16} className="mr-2" />
            Medidores
          </TabsTrigger>
          <TabsTrigger value="lecturas" className="rounded-none data-[state=active]:bg-white">
            <FileText size={16} className="mr-2" />
            Lecturas y Recibos
          </TabsTrigger>
        </TabsList>

        {/* Medidores Tab */}
        <TabsContent value="medidores">
          <div className="action-bar">
            <span className="text-slate-500">{meters.length} medidores registrados</span>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="rounded-none"
                onClick={() => setShowPriceModal(true)}
                data-testid="configure-price-btn"
              >
                <DollarSign size={16} className="mr-2" />
                Configurar Precio
              </Button>
              <Button 
                className="btn-industrial bg-slate-900 text-white hover:bg-slate-800"
                onClick={() => setShowMeterModal(true)}
                data-testid="add-meter-btn"
              >
                <Plus size={16} className="mr-2" />
                Agregar Medidor
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="card-industrial animate-pulse">
              <div className="h-64 bg-slate-100"></div>
            </div>
          ) : meters.length === 0 ? (
            <div className="empty-state py-16 card-industrial">
              <div className="empty-state-icon">
                <Gauge size={24} className="text-slate-400" />
              </div>
              <h3>No hay medidores registrados</h3>
              <p>Comienza agregando medidores de agua para cada lote.</p>
            </div>
          ) : (
            <div className="card-industrial overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="table-blueprint" data-testid="meters-table">
                  <thead>
                    <tr>
                      <th>No. Medidor</th>
                      <th>Lote</th>
                      <th>Propietario</th>
                      <th>Ubicación</th>
                      <th>Estado</th>
                      <th className="w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {meters.map((meter, index) => (
                      <tr key={meter.id} className="animate-slideIn" style={{ animationDelay: `${index * 20}ms` }}>
                        <td className="font-mono font-medium">{meter.numero_medidor}</td>
                        <td className="font-mono">{meter.numero_lote}</td>
                        <td>{meter.propietario_nombre || '-'}</td>
                        <td className="text-slate-500">{meter.ubicacion || '-'}</td>
                        <td>
                          <span className={`px-2 py-1 text-xs font-medium rounded ${meter.activo ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                            {meter.activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td>
                          <button
                            onClick={() => handleDeleteMeter(meter.id)}
                            className="p-2 hover:bg-red-50 transition-colors text-red-500"
                            data-testid={`delete-meter-${meter.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Lecturas Tab */}
        <TabsContent value="lecturas">
          <div className="action-bar">
            <div className="flex items-center gap-4">
              <span className="text-slate-500">{filteredReadings.length} lecturas</span>
              <Select value={filterMes} onValueChange={setFilterMes}>
                <SelectTrigger className="w-40 rounded-none" data-testid="filter-mes">
                  <SelectValue placeholder="Todos los meses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los meses</SelectItem>
                  {meses.map(m => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              {selectedReadings.length > 0 && (
                <Button
                  variant="destructive"
                  className="rounded-none"
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={deleting}
                  data-testid="delete-selected-readings-btn"
                >
                  <Trash2 size={16} className="mr-2" />
                  {deleting ? 'Eliminando...' : `Eliminar (${selectedReadings.length})`}
                </Button>
              )}
              <Button 
                className="btn-industrial bg-slate-900 text-white hover:bg-slate-800"
                onClick={() => { resetReadingForm(); setShowReadingModal(true); }}
                data-testid="add-reading-btn"
                disabled={meters.length === 0}
              >
                <Plus size={16} className="mr-2" />
                Registrar Lectura
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="card-industrial animate-pulse">
              <div className="h-64 bg-slate-100"></div>
            </div>
          ) : filteredReadings.length === 0 ? (
            <div className="empty-state py-16 card-industrial">
              <div className="empty-state-icon">
                <FileText size={24} className="text-slate-400" />
              </div>
              <h3>No hay lecturas registradas</h3>
              <p>{meters.length === 0 ? 'Primero agrega medidores de agua.' : 'Comienza registrando las lecturas mensuales.'}</p>
            </div>
          ) : (
            <div className="card-industrial overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="table-blueprint" data-testid="readings-table">
                  <thead>
                    <tr>
                      <th className="w-12">
                        <Checkbox
                          checked={selectedReadings.length === filteredReadings.length && filteredReadings.length > 0}
                          onCheckedChange={toggleSelectAllReadings}
                          data-testid="select-all-readings"
                        />
                      </th>
                      <th>Fecha</th>
                      <th>Medidor</th>
                      <th>Lote</th>
                      <th>Período</th>
                      <th className="text-right">Anterior</th>
                      <th className="text-right">Actual</th>
                      <th className="text-right">Consumo</th>
                      <th className="text-right">Total</th>
                      <th className="text-center">Foto</th>
                      <th className="text-center">Estado</th>
                      <th className="w-32"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReadings.map((reading, index) => (
                      <tr 
                        key={reading.id} 
                        className={`animate-slideIn ${selectedReadings.includes(reading.id) ? 'bg-amber-50' : ''}`}
                        style={{ animationDelay: `${index * 20}ms` }}
                      >
                        <td>
                          <Checkbox
                            checked={selectedReadings.includes(reading.id)}
                            onCheckedChange={() => toggleReadingSelection(reading.id)}
                            data-testid={`select-reading-${reading.id}`}
                          />
                        </td>
                        <td className="font-mono text-sm">{reading.fecha_lectura}</td>
                        <td className="font-mono">{reading.numero_medidor}</td>
                        <td className="font-mono font-medium">{reading.numero_lote}</td>
                        <td>{meses.find(m => m.value === reading.mes)?.label} {reading.año}</td>
                        <td className="text-right tabular-nums">{reading.lectura_anterior}</td>
                        <td className="text-right tabular-nums">{reading.lectura_actual}</td>
                        <td className="text-right tabular-nums text-blue-600 font-medium">{reading.consumo_m3} m³</td>
                        <td className="text-right tabular-nums text-emerald-600 font-medium">{formatCurrency(reading.monto_total)}</td>
                        <td className="text-center">
                          {reading.foto_url ? (
                            <span className="text-emerald-500">✓</span>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                        <td className="text-center">
                          {reading.estado_pago === 'pagado' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-emerald-100 text-emerald-700">
                              <CheckCircle size={12} />
                              Pagado
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-medium rounded bg-amber-100 text-amber-700">
                              Pendiente
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleViewReceipt(reading)}
                              className="p-2 hover:bg-slate-100 transition-colors text-slate-600"
                              title="Ver recibo"
                              data-testid={`view-receipt-${reading.id}`}
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              onClick={() => handleEditReading(reading)}
                              className="p-2 hover:bg-slate-100 transition-colors text-blue-600"
                              title="Editar lectura"
                              data-testid={`edit-reading-${reading.id}`}
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => handleDownloadPDF(reading)}
                              className="p-2 hover:bg-slate-100 transition-colors text-slate-600"
                              title="Descargar PDF"
                              data-testid={`download-receipt-${reading.id}`}
                            >
                              <Download size={16} />
                            </button>
                            {reading.estado_pago !== 'pagado' && (
                              <button
                                onClick={() => handleOpenPayment(reading)}
                                className="p-2 hover:bg-emerald-50 transition-colors text-emerald-600"
                                title="Registrar pago"
                                data-testid={`pay-reading-${reading.id}`}
                              >
                                <CreditCard size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Meter Modal */}
      <Dialog open={showMeterModal} onOpenChange={setShowMeterModal}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Agregar Medidor</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateMeter}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Número de Medidor *</Label>
                <Input
                  value={meterForm.numero_medidor}
                  onChange={(e) => setMeterForm(prev => ({ ...prev, numero_medidor: e.target.value }))}
                  placeholder="Ej: MED-001"
                  required
                  className="rounded-none"
                  data-testid="meter-numero-input"
                />
              </div>
              <div>
                <Label>Lote *</Label>
                <Select 
                  value={meterForm.lot_id} 
                  onValueChange={(val) => setMeterForm(prev => ({ ...prev, lot_id: val }))}
                  required
                >
                  <SelectTrigger className="rounded-none" data-testid="meter-lot-select">
                    <SelectValue placeholder="Seleccionar lote" />
                  </SelectTrigger>
                  <SelectContent>
                    {lots.map(lot => (
                      <SelectItem key={lot.id} value={lot.id}>
                        {lot.numero_lote} - {lot.propietario_nombre || 'Sin propietario'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ubicación (opcional)</Label>
                <Input
                  value={meterForm.ubicacion}
                  onChange={(e) => setMeterForm(prev => ({ ...prev, ubicacion: e.target.value }))}
                  placeholder="Ej: Entrada principal"
                  className="rounded-none"
                  data-testid="meter-ubicacion-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowMeterModal(false)} className="rounded-none">
                Cancelar
              </Button>
              <Button type="submit" className="btn-industrial bg-slate-900 text-white hover:bg-slate-800" data-testid="submit-meter-btn">
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Reading Modal */}
      <Dialog open={showReadingModal} onOpenChange={setShowReadingModal}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Registrar Lectura</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateReading}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Medidor *</Label>
                  <Select 
                    value={readingForm.meter_id} 
                    onValueChange={handleMeterSelect}
                    required
                  >
                    <SelectTrigger className="rounded-none" data-testid="reading-meter-select">
                      <SelectValue placeholder="Seleccionar medidor" />
                    </SelectTrigger>
                    <SelectContent>
                      {meters.filter(m => m.activo).map(meter => (
                        <SelectItem key={meter.id} value={meter.id}>
                          {meter.numero_medidor} - {meter.numero_lote}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fecha de Lectura *</Label>
                  <Input
                    type="date"
                    value={readingForm.fecha_lectura}
                    onChange={(e) => setReadingForm(prev => ({ ...prev, fecha_lectura: e.target.value }))}
                    required
                    className="rounded-none"
                    data-testid="reading-fecha-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Mes *</Label>
                  <Select 
                    value={readingForm.mes.toString()} 
                    onValueChange={(val) => setReadingForm(prev => ({ ...prev, mes: parseInt(val) }))}
                  >
                    <SelectTrigger className="rounded-none" data-testid="reading-mes-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {meses.map(m => (
                        <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Año *</Label>
                  <Input
                    type="number"
                    value={readingForm.año}
                    onChange={(e) => setReadingForm(prev => ({ ...prev, año: parseInt(e.target.value) }))}
                    required
                    className="rounded-none"
                    data-testid="reading-año-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Lectura Anterior *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={readingForm.lectura_anterior}
                    onChange={(e) => setReadingForm(prev => ({ ...prev, lectura_anterior: e.target.value }))}
                    required
                    className="rounded-none"
                    data-testid="reading-anterior-input"
                  />
                </div>
                <div>
                  <Label>Lectura Actual *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={readingForm.lectura_actual}
                    onChange={(e) => setReadingForm(prev => ({ ...prev, lectura_actual: e.target.value }))}
                    required
                    className="rounded-none"
                    data-testid="reading-actual-input"
                  />
                </div>
              </div>

              {/* Consumption preview */}
              {readingForm.lectura_anterior && readingForm.lectura_actual && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-blue-700">Consumo estimado:</span>
                    <span className="font-bold text-blue-700">
                      {(parseFloat(readingForm.lectura_actual) - parseFloat(readingForm.lectura_anterior)).toFixed(2)} m³
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-sm text-blue-700">Precio por m³:</span>
                    <span className="font-medium text-blue-700">{formatCurrency(currentPrice)}</span>
                  </div>
                  <div className="flex justify-between items-center mt-1 pt-2 border-t border-blue-200">
                    <span className="text-sm text-blue-700">Total estimado (con IVA):</span>
                    <span className="font-bold text-blue-700">
                      {formatCurrency(
                        (parseFloat(readingForm.lectura_actual) - parseFloat(readingForm.lectura_anterior)) 
                        * currentPrice * 1.16
                      )}
                    </span>
                  </div>
                </div>
              )}

              {/* Photo upload */}
              <div>
                <Label>Fotografía del Medidor *</Label>
                <div className="mt-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoSelect}
                    className="hidden"
                    data-testid="reading-photo-input"
                  />
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                      ${tempPhoto ? 'border-emerald-300 bg-emerald-50' : 'border-slate-300 hover:border-slate-400'}`}
                  >
                    {tempPhoto ? (
                      <div className="flex items-center justify-center gap-2">
                        <Camera size={20} className="text-emerald-600" />
                        <span className="text-emerald-700 font-medium">{tempPhoto.name}</span>
                      </div>
                    ) : (
                      <div>
                        <Camera size={32} className="mx-auto text-slate-400 mb-2" />
                        <p className="text-sm text-slate-500">Haz clic para seleccionar la fotografía</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowReadingModal(false)} className="rounded-none">
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="btn-industrial bg-slate-900 text-white hover:bg-slate-800" 
                data-testid="submit-reading-btn"
                disabled={!tempPhoto}
              >
                Guardar y Generar Recibo
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Price Configuration Modal */}
      <Dialog open={showPriceModal} onOpenChange={setShowPriceModal}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Configurar Precio del Agua</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePrice}>
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-600">Precio actual:</p>
                <p className="text-2xl font-bold">{formatCurrency(currentPrice)} / m³</p>
              </div>
              
              <div>
                <Label>Nuevo Precio por m³ *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={priceForm.precio_m3}
                  onChange={(e) => setPriceForm(prev => ({ ...prev, precio_m3: e.target.value }))}
                  placeholder="0.00"
                  required
                  className="rounded-none"
                  data-testid="price-m3-input"
                />
              </div>
              <div>
                <Label>Fecha de Vigencia *</Label>
                <Input
                  type="date"
                  value={priceForm.fecha_vigencia}
                  onChange={(e) => setPriceForm(prev => ({ ...prev, fecha_vigencia: e.target.value }))}
                  required
                  className="rounded-none"
                  data-testid="price-fecha-input"
                />
                <p className="text-xs text-slate-500 mt-1">El nuevo precio se aplicará a partir de esta fecha.</p>
              </div>

              {priceHistory.length > 0 && (
                <div className="mt-4">
                  <Label>Historial de Precios</Label>
                  <div className="mt-2 max-h-32 overflow-y-auto">
                    {priceHistory.slice(0, 5).map((p, i) => (
                      <div key={i} className="flex justify-between text-sm py-1 border-b border-slate-100">
                        <span className="text-slate-500">{p.fecha_vigencia}</span>
                        <span className="font-medium">{formatCurrency(p.precio_m3)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPriceModal(false)} className="rounded-none">
                Cancelar
              </Button>
              <Button type="submit" className="btn-industrial bg-slate-900 text-white hover:bg-slate-800" data-testid="submit-price-btn">
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Recibo de Agua</DialogTitle>
          </DialogHeader>
          {selectedReading && (
            <div className="py-4">
              <div className="border border-slate-200 rounded-lg p-6">
                {/* Receipt Header */}
                <div className="text-center border-b pb-4 mb-4">
                  <h2 className="text-xl font-bold">NAVE</h2>
                  <p className="text-sm text-slate-500">{selectedReading.park_nombre || selectedPark?.nombre}</p>
                  <p className="text-lg font-bold mt-2">Recibo de Consumo de Agua</p>
                </div>

                {/* Receipt Details */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Lote:</p>
                    <p className="font-bold">{selectedReading.numero_lote}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Propietario:</p>
                    <p className="font-bold">{selectedReading.propietario_nombre || 'No asignado'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Medidor:</p>
                    <p className="font-bold">{selectedReading.numero_medidor}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Período:</p>
                    <p className="font-bold">{meses.find(m => m.value === selectedReading.mes)?.label} {selectedReading.año}</p>
                  </div>
                </div>

                {/* Readings */}
                <div className="mt-4 bg-slate-50 p-4 rounded-lg">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-slate-500">Lectura Anterior</p>
                      <p className="text-lg font-bold">{selectedReading.lectura_anterior}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Lectura Actual</p>
                      <p className="text-lg font-bold">{selectedReading.lectura_actual}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Consumo</p>
                      <p className="text-lg font-bold text-blue-600">{selectedReading.consumo_m3} m³</p>
                    </div>
                  </div>
                </div>

                {/* Photo */}
                {selectedReading.foto_url && (
                  <div className="mt-4">
                    <p className="text-sm text-slate-500 mb-2">Evidencia fotográfica:</p>
                    <img 
                      src={selectedReading.foto_url} 
                      alt="Lectura del medidor"
                      className="max-h-48 rounded-lg border border-slate-200 mx-auto"
                    />
                  </div>
                )}

                {/* Amounts */}
                <div className="mt-4 border-t pt-4">
                  <div className="flex justify-between py-1">
                    <span className="text-slate-500">Precio por m³:</span>
                    <span>{formatCurrency(selectedReading.precio_m3)}</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-slate-500">Subtotal (Base):</span>
                    <span>{formatCurrency(selectedReading.monto_base)}</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-slate-500">IVA (16%):</span>
                    <span>{formatCurrency(selectedReading.monto_iva)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-t mt-2 text-lg font-bold">
                    <span>Total a Pagar:</span>
                    <span className="text-emerald-600">{formatCurrency(selectedReading.monto_total)}</span>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-4 text-center text-xs text-slate-400">
                  <p>Fecha de lectura: {selectedReading.fecha_lectura}</p>
                  <p>Generado: {new Date(selectedReading.created_at).toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceiptModal(false)} className="rounded-none">
              Cerrar
            </Button>
            <Button 
              onClick={() => selectedReading && handleDownloadPDF(selectedReading)}
              className="btn-industrial bg-slate-900 text-white hover:bg-slate-800"
            >
              <Download size={16} className="mr-2" />
              Descargar PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Reading Modal */}
      <Dialog open={showEditReadingModal} onOpenChange={setShowEditReadingModal}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Editar Lectura</DialogTitle>
          </DialogHeader>
          {editingReading && (
            <form onSubmit={handleUpdateReading}>
              <div className="space-y-4 py-4">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-sm text-slate-500">Medidor: <span className="font-medium text-slate-900">{editingReading.numero_medidor}</span></p>
                  <p className="text-sm text-slate-500">Lote: <span className="font-medium text-slate-900">{editingReading.numero_lote}</span></p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Fecha de Lectura *</Label>
                    <Input
                      type="date"
                      value={editingReading.fecha_lectura}
                      onChange={(e) => setEditingReading(prev => ({ ...prev, fecha_lectura: e.target.value }))}
                      required
                      className="rounded-none"
                      data-testid="edit-reading-fecha-input"
                    />
                  </div>
                  <div>
                    <Label>Mes *</Label>
                    <Select 
                      value={editingReading.mes.toString()} 
                      onValueChange={(val) => setEditingReading(prev => ({ ...prev, mes: parseInt(val) }))}
                    >
                      <SelectTrigger className="rounded-none" data-testid="edit-reading-mes-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {meses.map(m => (
                          <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Lectura Anterior *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingReading.lectura_anterior}
                      onChange={(e) => setEditingReading(prev => ({ ...prev, lectura_anterior: e.target.value }))}
                      required
                      className="rounded-none"
                      data-testid="edit-reading-anterior-input"
                    />
                  </div>
                  <div>
                    <Label>Lectura Actual *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingReading.lectura_actual}
                      onChange={(e) => setEditingReading(prev => ({ ...prev, lectura_actual: e.target.value }))}
                      required
                      className="rounded-none"
                      data-testid="edit-reading-actual-input"
                    />
                  </div>
                </div>

                {/* Consumption preview */}
                {editingReading.lectura_anterior && editingReading.lectura_actual && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-700">Consumo:</span>
                      <span className="font-bold text-blue-700">
                        {(parseFloat(editingReading.lectura_actual) - parseFloat(editingReading.lectura_anterior)).toFixed(2)} m³
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-sm text-blue-700">Precio por m³:</span>
                      <span className="font-medium text-blue-700">{formatCurrency(editingReading.precio_m3)}</span>
                    </div>
                    <div className="flex justify-between items-center mt-1 pt-2 border-t border-blue-200">
                      <span className="text-sm text-blue-700">Total estimado (con IVA):</span>
                      <span className="font-bold text-blue-700">
                        {formatCurrency(
                          (parseFloat(editingReading.lectura_actual) - parseFloat(editingReading.lectura_anterior)) 
                          * editingReading.precio_m3 * 1.16
                        )}
                      </span>
                    </div>
                  </div>
                )}

                {/* Current photo */}
                {editingReading.foto_url && (
                  <div>
                    <Label>Fotografía Actual</Label>
                    <div className="mt-2">
                      <img 
                        src={editingReading.foto_url} 
                        alt="Lectura actual"
                        className="max-h-32 rounded-lg border border-slate-200"
                      />
                    </div>
                  </div>
                )}

                {/* New photo upload */}
                <div>
                  <Label>Cambiar Fotografía (opcional)</Label>
                  <div className="mt-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoSelect}
                      className="hidden"
                      id="edit-photo-input"
                      data-testid="edit-reading-photo-input"
                    />
                    <label 
                      htmlFor="edit-photo-input"
                      className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors block
                        ${tempPhoto ? 'border-emerald-300 bg-emerald-50' : 'border-slate-300 hover:border-slate-400'}`}
                    >
                      {tempPhoto ? (
                        <div className="flex items-center justify-center gap-2">
                          <Camera size={20} className="text-emerald-600" />
                          <span className="text-emerald-700 font-medium">{tempPhoto.name}</span>
                        </div>
                      ) : (
                        <div>
                          <Camera size={24} className="mx-auto text-slate-400 mb-1" />
                          <p className="text-sm text-slate-500">Clic para seleccionar nueva foto</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setShowEditReadingModal(false); setTempPhoto(null); }} className="rounded-none">
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="btn-industrial bg-slate-900 text-white hover:bg-slate-800" 
                  data-testid="submit-edit-reading-btn"
                >
                  Guardar Cambios
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Registrar Pago de Agua</DialogTitle>
          </DialogHeader>
          {payingReading && (
            <form onSubmit={handleRegisterPayment}>
              <div className="space-y-4 py-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-blue-600">Medidor:</p>
                      <p className="font-bold">{payingReading.numero_medidor}</p>
                    </div>
                    <div>
                      <p className="text-blue-600">Lote:</p>
                      <p className="font-bold">{payingReading.numero_lote}</p>
                    </div>
                    <div>
                      <p className="text-blue-600">Período:</p>
                      <p className="font-bold">{meses.find(m => m.value === payingReading.mes)?.label} {payingReading.año}</p>
                    </div>
                    <div>
                      <p className="text-blue-600">Consumo:</p>
                      <p className="font-bold">{payingReading.consumo_m3} m³</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <div className="flex justify-between text-sm">
                      <span className="text-blue-600">Subtotal:</span>
                      <span>{formatCurrency(payingReading.monto_base)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-blue-600">IVA (16%):</span>
                      <span>{formatCurrency(payingReading.monto_iva)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg mt-1">
                      <span className="text-blue-700">Total a Pagar:</span>
                      <span className="text-blue-700">{formatCurrency(payingReading.monto_total)}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Fecha de Pago *</Label>
                  <Input
                    type="date"
                    value={paymentForm.fecha}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, fecha: e.target.value }))}
                    required
                    className="rounded-none"
                    data-testid="water-payment-fecha"
                  />
                </div>

                <div>
                  <Label>Método de Pago *</Label>
                  <Select 
                    value={paymentForm.metodo_pago} 
                    onValueChange={(val) => setPaymentForm(prev => ({ ...prev, metodo_pago: val }))}
                  >
                    <SelectTrigger className="rounded-none" data-testid="water-payment-metodo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transferencia">Transferencia</SelectItem>
                      <SelectItem value="efectivo">Efectivo</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="tarjeta">Tarjeta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Referencia (opcional)</Label>
                  <Input
                    value={paymentForm.referencia}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, referencia: e.target.value }))}
                    placeholder="Número de transferencia, cheque, etc."
                    className="rounded-none"
                    data-testid="water-payment-referencia"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowPaymentModal(false)} className="rounded-none">
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="btn-industrial bg-emerald-600 text-white hover:bg-emerald-700" 
                  data-testid="submit-water-payment-btn"
                >
                  <CreditCard size={16} className="mr-2" />
                  Registrar Pago
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar lecturas seleccionadas?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminarán {selectedReadings.length} lecturas y sus recibos permanentemente. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSelectedReadings}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Agua;
